import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, FileText, Banknote,
  CreditCard, RefreshCcw, Image as ImageIcon, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import CompteSelect from "@/components/CompteSelect";

const STATUT_LABELS = {
  brouillon: "Brouillon",
  a_valider: "À valider",
  comptabilisee: "Comptabilisée",
  classee: "Classée",
  rejetee: "Rejetée",
};
const STATUT_COLORS = {
  a_valider: "bg-amber-100 text-amber-800 border-amber-300",
  comptabilisee: "bg-emerald-100 text-emerald-800 border-emerald-300",
  classee: "bg-blue-100 text-blue-800 border-blue-300",
  rejetee: "bg-red-100 text-red-800 border-red-300",
};

const MODES = [
  { v: "cash", label: "Caisse", icon: Banknote, compte: "1000" },
  { v: "carte_raiffeisen", label: "Carte Raiffeisen", icon: CreditCard, compte: "1020" },
];

// iter327 — Seule question posée pour une sortie de caisse : elle choisit le compte.
const NATURES = [
  { v: "marchandise", label: "Marchandise", compte: "4200" },
  { v: "representation", label: "Frais de représentation (restaurant)", compte: "6640" },
  { v: "prestation_tiers", label: "Prestation de service d'un tiers", compte: "4400" },
];

export default function DepenseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);

  // Form fields
  const [fournisseur, setFournisseur] = useState("");
  const [dateDep, setDateDep] = useState("");
  const [montantTtc, setMontantTtc] = useState("");
  const [tvaTaux, setTvaTaux] = useState("8.1");
  const [montantHt, setMontantHt] = useState("");
  const [tvaMontant, setTvaMontant] = useState("");
  const [description, setDescription] = useState("");
  const [nature, setNature] = useState("");
  const [modePaiement, setModePaiement] = useState("");
  const [notes, setNotes] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/depenses-rapides/${id}`);
      setD(data);
      setFournisseur(data.fournisseur_nom || "");
      setDateDep(data.date_depense || "");
      setMontantTtc(data.montant_ttc != null ? String(data.montant_ttc) : "");
      setTvaTaux(data.tva_taux != null ? String(data.tva_taux) : "8.1");
      setMontantHt(data.montant_ht != null ? String(data.montant_ht) : "");
      setTvaMontant(data.tva_montant != null ? String(data.tva_montant) : "");
      setDescription(data.description || "");
      setNature(data.nature || "");
      setModePaiement(data.mode_paiement || "");
      setNotes(data.notes || "");
    } catch {
      toast.error("Dépense introuvable");
      navigate("/depenses-rapides");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-calcul TVA quand TTC + taux changent
  useEffect(() => {
    const ttc = parseFloat(montantTtc);
    const taux = parseFloat(tvaTaux);
    if (ttc > 0 && taux > 0) {
      const ht = ttc / (1 + taux / 100);
      setMontantHt(ht.toFixed(2));
      setTvaMontant((ttc - ht).toFixed(2));
    } else if (ttc > 0 && (taux === 0 || isNaN(taux))) {
      setMontantHt(ttc.toFixed(2));
      setTvaMontant("0.00");
    }
  }, [montantTtc, tvaTaux]);

  const sauvegarder = async () => {
    setSaving(true);
    try {
      const modeInfo = MODES.find((m) => m.v === modePaiement);
      const natureInfo = NATURES.find((n) => n.v === nature);
      const payload = {
        fournisseur_nom: fournisseur || null,
        date_depense: dateDep || null,
        montant_ttc: parseFloat(montantTtc) || null,
        tva_taux: parseFloat(tvaTaux) || 0,
        tva_montant: parseFloat(tvaMontant) || 0,
        montant_ht: parseFloat(montantHt) || null,
        description: description || null,
        nature: nature || null,
        compte_charge: natureInfo?.compte || null,
        compte_tva: "1170",
        mode_paiement: modePaiement || null,
        compte_paiement: modeInfo?.compte || null,
        notes: notes || null,
      };
      await api.patch(`/depenses-rapides/${id}`, payload);
      toast.success("Modifications enregistrées");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur sauvegarde");
    } finally { setSaving(false); }
  };

  const valider = async () => {
    if (!fournisseur || !dateDep || !montantTtc || !modePaiement) {
      toast.error("Champs requis manquants : fournisseur, date, montant TTC, mode de paiement");
      return;
    }
    if (modePaiement === "cash" && !nature) {
      toast.error("Choisis la nature : marchandise, représentation ou prestation de tiers");
      return;
    }
    if (!window.confirm("Valider cette dépense ?")) return;
    // Sauvegarde puis valide
    await sauvegarder();
    try {
      await api.post(`/depenses-rapides/${id}/valider`);
      const { data } = await api.get(`/depenses-rapides/${id}`);
      if (data.statut === "comptabilisee") {
        toast.success("Dépense comptabilisée — écriture créée");
      } else if (data.statut === "classee") {
        toast.success("Justificatif classé — aucune écriture (carte Raiffeisen)");
      }
      navigate("/depenses-rapides");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur validation");
    }
  };

  const rejeter = async () => {
    const motif = window.prompt("Motif du rejet (obligatoire) :");
    if (!motif) return;
    try {
      await api.post(`/depenses-rapides/${id}/rejeter`, { motif });
      toast.success("Dépense rejetée");
      navigate("/depenses-rapides");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const relancerOcr = async () => {
    setOcrLoading(true);
    try {
      await api.post(`/depenses-rapides/${id}/ocr?force=true`);
      toast.success("OCR relancé");
      reload();
    } catch {
      toast.error("OCR échoué");
    } finally { setOcrLoading(false); }
  };

  if (loading || !d) {
    return <div className="p-8 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const readonly = ["comptabilisee", "classee", "rejetee"].includes(d.statut);
  const cfg = STATUT_COLORS[d.statut] || "bg-gray-100 text-gray-700";
  const fichier0 = d.fichiers?.[0];
  const apiUrl = process.env.REACT_APP_BACKEND_URL || "";
  const previewUrl = fichier0
    ? `${apiUrl}/api/depenses-rapides/${id}/file/0?variant=preview`
    : null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="page-depense-detail">
      {/* iter254 — mobile : titre sur une ligne, actions en grille pleine largeur */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate("/depenses-rapides")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 font-mono">{d.reference_interne}</h1>
            <Badge className={`mt-1 ${cfg} text-[11px]`}>
              {STATUT_LABELS[d.statut] || d.statut}
            </Badge>
          </div>
        </div>
        {!readonly && (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2 sm:flex-shrink-0">
            <Button variant="outline" size="sm" onClick={relancerOcr} disabled={ocrLoading} data-testid="dep-relancer-ocr">
              {ocrLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Relancer OCR
            </Button>
            <Button variant="outline" size="sm" className="text-red-700 border-red-200" onClick={rejeter} data-testid="dep-rejeter">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
            </Button>
            <Button variant="outline" size="sm" onClick={sauvegarder} disabled={saving} data-testid="dep-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Enregistrer
            </Button>
            <Button size="sm" className="bg-[#52a675] hover:bg-[#3d8a5c]" onClick={valider} data-testid="dep-valider">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Valider
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Image / PDF */}
        <Card className="p-4 lg:sticky lg:top-4 lg:max-h-[85vh] overflow-y-auto">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" /> Justificatif
          </h3>
          {fichier0 ? (
            fichier0.mime?.startsWith("image/") || fichier0.preview_path_rel ? (
              <img
                src={previewUrl}
                alt={fichier0.nom}
                className="w-full rounded border border-gray-200"
                data-testid="dep-preview-img"
              />
            ) : (
              <a
                href={`${apiUrl}/api/depenses-rapides/${id}/file/0`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm"
              >
                <FileText className="w-4 h-4" /> Ouvrir le PDF
              </a>
            )
          ) : (
            <p className="text-sm text-gray-400 italic">Aucun fichier</p>
          )}
          {d.donnees_ocr?.confiance > 0 && (
            <div className="mt-3 text-[11px] text-gray-500 bg-blue-50 p-2 rounded">
              <Sparkles className="w-3 h-3 inline mr-1 text-blue-500" />
              IA : confiance {Math.round((d.donnees_ocr.confiance || 0) * 100)}%
              {d.donnees_ocr.categorie_suggere && (
                <span className="ml-2 text-blue-700">· catégorie : {d.donnees_ocr.categorie_suggere}</span>
              )}
            </div>
          )}
        </Card>

        {/* Form fields */}
        <Card className="p-4">
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3">Détails de la dépense</h3>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Fournisseur / Commerce *</Label>
              <Input
                value={fournisseur}
                onChange={(e) => setFournisseur(e.target.value)}
                disabled={readonly}
                placeholder="Ex: Migros, Shell, AVIA…"
                data-testid="dep-fournisseur"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input
                  type="date" value={dateDep}
                  onChange={(e) => setDateDep(e.target.value)}
                  disabled={readonly}
                  data-testid="dep-date"
                />
              </div>
              <div>
                <Label className="text-xs">Montant TTC *</Label>
                <Input
                  type="number" step="0.05" min="0"
                  value={montantTtc}
                  onChange={(e) => setMontantTtc(e.target.value)}
                  disabled={readonly}
                  placeholder="0.00"
                  data-testid="dep-montant-ttc"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Taux TVA</Label>
                <CompteSelect
                  value={tvaTaux} onValueChange={setTvaTaux} disabled={readonly}
                  options={[
                    { value: "8.1", label: "8.1 % (standard)" },
                    { value: "3.8", label: "3.8 % (hébergement)" },
                    { value: "2.6", label: "2.6 % (alim/medic)" },
                    { value: "0", label: "0 % (exonéré)" },
                  ]}
                  data-testid="dep-tva-taux"
                />
              </div>
              <div>
                <Label className="text-xs">HT (auto)</Label>
                <Input
                  type="number" step="0.01"
                  value={montantHt}
                  onChange={(e) => setMontantHt(e.target.value)}
                  disabled={readonly}
                  className="bg-gray-50"
                  data-testid="dep-montant-ht"
                />
              </div>
              <div>
                <Label className="text-xs">TVA (auto)</Label>
                <Input
                  type="number" step="0.01"
                  value={tvaMontant}
                  onChange={(e) => setTvaMontant(e.target.value)}
                  disabled={readonly}
                  className="bg-gray-50"
                  data-testid="dep-tva-montant"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={readonly}
                placeholder="Ex: Plein essence VW Tiguan, Repas équipe…"
                data-testid="dep-description"
              />
            </div>

            <div className="pt-3 border-t border-gray-200">
              <Label className="text-xs uppercase tracking-wider mb-2 block">Mode de paiement *</Label>
              <div className="grid grid-cols-2 gap-2">
                {MODES.map((m) => {
                  const Icon = m.icon;
                  const active = modePaiement === m.v;
                  return (
                    <button
                      key={m.v}
                      type="button"
                      onClick={() => !readonly && setModePaiement(m.v)}
                      disabled={readonly}
                      className={`flex items-center gap-2 p-2.5 rounded-lg border-2 text-sm transition ${
                        active
                          ? "border-orange-500 bg-orange-50 text-orange-900 font-semibold"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      } ${readonly ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`dep-mode-${m.v}`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {modePaiement === "cash" && (
                <div className="mt-3">
                  <Label className="text-xs uppercase tracking-wider mb-2 block">
                    Nature de la dépense *
                  </Label>
                  <div className="grid gap-2">
                    {NATURES.map((n) => {
                      const active = nature === n.v;
                      return (
                        <button
                          key={n.v}
                          type="button"
                          onClick={() => !readonly && setNature(n.v)}
                          disabled={readonly}
                          className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border-2 text-sm text-left transition ${
                            active
                              ? "border-orange-500 bg-orange-50 text-orange-900 font-semibold"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          } ${readonly ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                          data-testid={`dep-nature-${n.v}`}
                        >
                          <span>{n.label}</span>
                          <span className="text-[11px] text-gray-400">{n.compte}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {modePaiement && (
                <p className="text-[11px] text-gray-500 mt-2" data-testid="dep-mode-hint">
                  {modePaiement === "cash"
                    ? "Écriture immédiate : charge au débit, caisse 1000 au crédit (TVA préalable séparée)."
                    : "Aucune écriture ici : la charge arrive par le relevé bancaire. Le ticket reste conservé comme justificatif."}
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">Notes internes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={readonly}
                rows={2}
                placeholder="Optionnel"
                data-testid="dep-notes"
              />
            </div>

            {/* Répartition analytique (charges étalées sur plusieurs mois) */}
            <RepartitionSection
              depense={d}
              onUpdated={reload}
              readonly={readonly}
            />

            {/* Infos comptabilisation si finalisée */}
            {d.statut === "comptabilisee" && (
              <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm">
                <p className="font-semibold text-emerald-900 mb-1">Dépense comptabilisée</p>
                <p className="text-xs text-emerald-800">
                  Le {fmtIso(d.comptabilise_at)} · écriture {d.ecriture_id?.slice(0, 8)}…
                </p>
                {d.camt_rapproche_at && (
                  <p className="text-xs text-emerald-800 mt-1">
                    Rapproché CAMT le {fmtIso(d.camt_rapproche_at)}
                  </p>
                )}
              </div>
            )}
            {d.statut === "classee" && (
              <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm">
                <p className="font-semibold text-blue-900 mb-1">Justificatif classé</p>
                <p className="text-xs text-blue-800">
                  Payée par carte Raiffeisen · aucune écriture dans le CRM, la charge arrive
                  par le relevé bancaire. Le ticket reste consultable ici.
                </p>
              </div>
            )}
            {d.statut === "rejetee" && d.rejet_motif && (
              <div className="bg-red-50 border border-red-200 p-3 rounded text-sm">
                <p className="font-semibold text-red-900 mb-1">Rejetée</p>
                <p className="text-xs text-red-800">{d.rejet_motif}</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function fmtIso(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("fr-CH");
  } catch { return s; }
}

// Composant Répartition analytique (réutilisable, doit être à la fin du fichier)
function RepartitionSection({ depense, onUpdated, readonly }) {
  const [showConfig, setShowConfig] = useState(false);
  const [type, setType] = useState(depense.repartition_analytique?.type || "annuel");
  const [dateDebut, setDateDebut] = useState(
    depense.repartition_analytique?.date_debut || depense.date_depense || ""
  );
  const [dateFin, setDateFin] = useState(depense.repartition_analytique?.date_fin || "");
  const [saving, setSaving] = useState(false);
  const rep = depense.repartition_analytique;
  const actif = rep?.actif;

  const apply = async () => {
    setSaving(true);
    try {
      await api.put(`/depenses-rapides/${depense.id}/repartition`, {
        actif: true, type, date_debut: dateDebut, date_fin: type === "personnalise" ? dateFin : null,
      });
      toast.success("Répartition analytique configurée");
      setShowConfig(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm("Désactiver la répartition analytique ? Le montant total sera réimputé sur le mois de la dépense.")) return;
    setSaving(true);
    try {
      await api.put(`/depenses-rapides/${depense.id}/repartition`, { actif: false });
      toast.success("Répartition désactivée");
      onUpdated();
    } catch {
      toast.error("Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="pt-3 border-t border-gray-200" data-testid="dep-repartition">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider">Répartition analytique</Label>
        {actif ? (
          <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">
            Active · {rep.type}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] text-gray-500">Non répartie</Badge>
        )}
      </div>
      {actif ? (
        <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
          <p className="text-[11px] text-purple-700 mb-1">
            Du {fmtIso(rep.date_debut)} au {fmtIso(rep.date_fin)} · {rep.montants_mensuels?.length} mois
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 mt-2 text-[10px] font-mono">
            {rep.montants_mensuels?.slice(0, 12).map((e) => (
              <div key={e.periode} className="bg-white border border-purple-100 rounded px-1.5 py-0.5">
                <span className="text-gray-500">{e.periode}</span>{" "}
                <span className="font-semibold text-purple-700">{e.montant}</span>
              </div>
            ))}
          </div>
          {!readonly && (
            <Button size="sm" variant="ghost" className="mt-2 text-red-600 h-7 text-xs"
              onClick={disable} disabled={saving} data-testid="dep-rep-disable">
              Désactiver la répartition
            </Button>
          )}
        </div>
      ) : (
        <>
          {!showConfig ? (
            !readonly && (
              <Button size="sm" variant="outline" className="text-xs"
                onClick={() => setShowConfig(true)} data-testid="dep-rep-config">
                Répartir cette charge dans le temps
              </Button>
            )
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
              <div>
                <Label className="text-xs">Type de répartition</Label>
                <select
                  value={type} onChange={(e) => setType(e.target.value)}
                  className="w-full mt-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  data-testid="dep-rep-type"
                >
                  <option value="mensuel">Mensuel (1 mois)</option>
                  <option value="trimestriel">Trimestriel (3 mois)</option>
                  <option value="annuel">Annuel (12 mois)</option>
                  <option value="personnalise">Personnalisé (plage libre)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Date début</Label>
                  <Input type="date" value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    data-testid="dep-rep-debut" />
                </div>
                {type === "personnalise" && (
                  <div>
                    <Label className="text-xs">Date fin</Label>
                    <Input type="date" value={dateFin}
                      onChange={(e) => setDateFin(e.target.value)}
                      data-testid="dep-rep-fin" />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={apply} disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700" data-testid="dep-rep-apply">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Appliquer
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowConfig(false)}>
                  Annuler
                </Button>
              </div>
              <p className="text-[10px] text-gray-500 italic">
                ℹ️ La répartition ne modifie pas la comptabilité officielle (1 écriture à la date réelle).
                Elle est utilisée uniquement pour les statistiques et dashboards mensuels.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
